import React from 'react';
import {
  Text,
  RichText,
  Link,
  withSitecoreContext,
  Image,
  Placeholder,
} from '@sitecore-jss/sitecore-jss-react';
import './styles.css';
import '../SharedUtils/themeColors/backgroundColor.css';
import { addBackgroundColorVariables } from '../SharedUtils/themeColors';
import RenderingStyling from '../../../lib/RenderingStyling';
import { addStyle, getTrackingAttributes } from '../SharedUtils/utils';
import { useInvertedColor } from '../SharedUtils/hooks/useInvertedColor';
import useHorizonalTabs from './horizonalTabsHook';
import { useSubtitleClasses } from '../FullWidthRichText/useSubtitleClasses';
import { renderSubTitle } from '../FullWidthRichText/fieldsRenderer';
import useTheme from '../SharedUtils/hooks';

const HorizontalTabs = ({ fields, rendering, sitecoreContext }) => {
  const isEditorMode = sitecoreContext.pageEditing;
  const { isLight } = useTheme();

  const widgetClasses = [
    'o-widget',
    'c-horizontal-tabs',
    'u-align-center',
    'background-color-theme',
  ];
  const widgetStyles = {};

  addBackgroundColorVariables(fields, widgetStyles);

  const panelTabClassList = ['c-horizontal-tabs-panelNav'];
  const panelStyleList = {};

  const horizontalTabPanelLists = fields?.HorizontalTabPanelList || [];
  const firstPanelItemId = horizontalTabPanelLists[0]?.id;

  if (
    horizontalTabPanelLists.some((tab) => {
      return tab.fields?.Image?.value?.src && tab.fields?.Image?.value.class !== 'scEmptyImage';
    })
  ) {
    panelTabClassList.push('c-horizontal-tabs-icon-nav');
  }

  const subTitleClasses = useSubtitleClasses('c-horizontal-tabs__subtitle', fields);

  // Styles
  if (fields?.PaddingTop?.value)
    addStyle(widgetStyles, '--horizontal-tab-padding-top', fields?.PaddingTop.value);
  if (fields?.PaddingBottom?.value)
    addStyle(widgetStyles, '--horizontal-tab-padding-bottom', fields?.PaddingBottom.value);

  if (fields?.HeightDesktop?.value)
    addStyle(panelStyleList, '--horizontal-tab-height-desktop', `${fields?.HeightDesktop.value}px`);
  if (fields?.HeightTablet?.value)
    addStyle(panelStyleList, '--horizontal-tab-height-tablet', `${fields?.HeightTablet.value}px`);
  if (fields?.HeightMobile?.value)
    addStyle(panelStyleList, '--horizontal-tab-height-mobile', `${fields?.HeightMobile.value}px`);
  if (fields?.CenterTabsForMobile?.value == true) panelTabClassList.push('center-tabs-in-mobile');
  const isColorInverted = useInvertedColor(fields);
  if (isColorInverted) widgetClasses.push('is-inverted');
  if (fields?.BackgroundImageCover?.value) widgetClasses.push('u-bg-align-center u-bg-size-cover');

  if (fields?.Image?.value?.src && fields?.Image?.value.class !== 'scEmptyImage') {
    addStyle(widgetStyles, 'backgroundImage', `url('${fields?.Image.value.src}')`);
  }

  // CTA default styles
  let ctaPrimaryCss = 'c-button c-button--cta';
  const ctaSecondaryCss = 'c-link c-link-cta';

  // Animation
  let animationEnterClassName = 'slideup fade';
  let animationExitClassName = 'slidedown fade';

  switch (fields.AnimatedTransition?.value) {
    case 'SlideLeftRight':
      animationEnterClassName = 'slideright';
      animationExitClassName = 'sliderleft';
      break;
    case 'ZoomInOut':
      animationEnterClassName = 'scaleup';
      animationExitClassName = 'scaledown';
      break;
  }

  const trackingAttributes = getTrackingAttributes(fields);

  useHorizonalTabs(isLight);

  return (
    <div
      {...RenderingStyling(rendering.componentName)}
      className={widgetClasses.join(' ')}
      style={widgetStyles}
      data-widget="HorizontalTabs"
      data-panelwrapper={`c-horizontal-tab-panelWrapper-${rendering.uid}`}
    >
      <div className="c-container">
        {renderSubTitle({ field: fields.SubTitle, classes: subTitleClasses })}

        {renderHeadline(fields, isEditorMode)}

        {renderDescription(fields, isEditorMode)}

        <NavigationTabs
          isLight={isLight}
          panelTabClassList={panelTabClassList}
          horizontalTabPanelLists={horizontalTabPanelLists}
          firstPanelItemId={firstPanelItemId}
          trackingAttributes={trackingAttributes}
        />

        {renderTabsContent()}
      </div>
    </div>
  );

  function renderTabsContent() {
    return (
      <div
        id={`c-horizontal-tab-panelWrapper-${rendering.uid}`}
        className="c-horizontal-tabs-panelWrapper"
        style={panelStyleList}
        data-enter={animationEnterClassName}
        data-exit={animationExitClassName}
        data-sequential="no"
      >
        {horizontalTabPanelLists.map((tabRaw, index) => {
          const tab = tabRaw.fields;
          const className = tabRaw.id === firstPanelItemId ? 'panel current' : 'panel';

          if (tab.CTASecondaryButtonStyle?.value) {
            ctaPrimaryCss = 'c-cta-button is-primary is-medium';
          }

          const dataTrackCTA = tab.CallToAction?.value?.text;
          const dataTrackCTASecBtn = tab.CallToActionSecondaryButton?.value?.text;

          const trackingAttributes = getTrackingAttributes(fields);
          const firstTracking = {
            'data-track': trackingAttributes['data-track'],
            'data-track-value': 'horizontaltab:' + tab.Title?.value + ':' + dataTrackCTA,
            'data-trackt-destination': trackingAttributes['data-trackt-destination'],
          };

          const secondTracking = {
            'data-track': trackingAttributes['data-track'],
            'data-track-value': 'horizontaltab:' + tab.Title?.value + ':' + dataTrackCTASecBtn,
            'data-trackt-destination': trackingAttributes['data-trackt-destination'],
          };

          return (
            <div className={className} id={`anchor-${tabRaw.id}`} key={tabRaw.id}>
              <div className="c-horizontal-tabs-panel-content">
                <div>
                  {tab.HeadlineRTE?.value && (
                    <div className="c-horizontal-tabs-panel-content__headline" tabIndex="0">
                      {tab.HeadlineSecondaryType?.value === 'H3' ? (
                        <h3>
                          <RichText field={tab.HeadlineRTE} />
                        </h3>
                      ) : (
                        <h2>
                          <RichText field={tab.HeadlineRTE} />
                        </h2>
                      )}
                    </div>
                  )}

                  {tab.Description?.value && (
                    <div className="c-horizontal-tabs-panel-content__description">
                      <RichText field={tab.Description} />
                    </div>
                  )}

                  <div className="c-horizontal-tabs-panel-content__cta">
                    {tab.CallToAction?.value?.text && (
                      <Link field={tab.CallToAction} className={ctaPrimaryCss} {...firstTracking} />
                    )}
                    {tab.CallToActionSecondaryButton?.value?.text && (
                      <Link
                        field={tab.CallToActionSecondaryButton}
                        className={ctaSecondaryCss}
                        {...secondTracking}
                      />
                    )}
                  </div>
                </div>

                <div>
                  {tab.HorizontalTabPanelItemList?.map((itemRaw, index) => {
                    return renderTabPanelItem(
                      itemRaw,
                      trackingAttributes,
                      tab,
                      index,
                      ctaSecondaryCss
                    );
                  })}
                </div>
              </div>
              <Placeholder
                name={`jss-multiple-content-${index}`}
                rendering={rendering}
                key={index}
              />
            </div>
          );
        })}
      </div>
    );
  }
};

export default withSitecoreContext()(HorizontalTabs);

const renderHeadline = (fields, isEditorMode) => {
  return (
    (fields.Headline?.value || isEditorMode) && (
      <div className="c-horizontal-tabs__headline" tabIndex="0">
        {fields.HeadlinePrimaryType?.value === 'H2' ? (
          <h2>
            <RichText field={fields.Headline} />
          </h2>
        ) : (
          <h1>
            <RichText field={fields.Headline} />
          </h1>
        )}
      </div>
    )
  );
};

const renderDescription = (fields, isEditorMode) => {
  return (
    (fields.Description?.value || isEditorMode) && (
      <div className="c-horizontal-tabs__description" tabIndex="0">
        <RichText field={fields.Description} />
      </div>
    )
  );
};

const NavigationTabs = ({
  isLight,
  panelTabClassList,
  horizontalTabPanelLists,
  firstPanelItemId,
  trackingAttributes,
}) => {
  return (
    <ul className={panelTabClassList.join(' ')}>
      {horizontalTabPanelLists.map((tabRaw) => {
        const tab = tabRaw.fields;

        const panelClassName = ['c-horizontal-tabs-panelNav-tab'];
        if (tabRaw.id === firstPanelItemId) panelClassName.push('is-active');

        const img = tab.Image?.value?.src && tab.Image?.value.class !== 'scEmptyImage' && (
          <img alt={tab.Image?.value?.alt || ''} src={tab.Image.value.src} />
        );

        const tabStyleOverride = {};

        if (tab.Image?.value?.src && tab.Image?.value.class !== 'scEmptyImage') {
          panelClassName.push('c-horizontal-tabs-icon-nav-tab');

          const invertedField = isLight ? tab.InvertedLight : tab.InvertedDark;
          if (invertedField?.value) panelClassName.push('is-inverted');

          const themeField = isLight ? tab.TabThemeLight : tab.TabThemeDark;
          if (themeField?.fields?.['ADS Color']?.value)
            panelClassName.push(`u-filter-${themeField?.fields?.['ADS Color']?.value}`);

          addStyle(
            tabStyleOverride,
            '--horizontal-icon-nav-tab-bg-color',
            isLight ? tab.BackgroundColorDefaultLight?.value : tab.BackgroundColorDefaultDark?.value
          );
          addStyle(
            tabStyleOverride,
            '--horizontal-icon-nav-tab-bg-hover-color',
            isLight ? tab.BackgroundColorHoverLight?.value : tab.BackgroundColorHoverDark?.value
          );
          addStyle(
            tabStyleOverride,
            '--horizontal-icon-nav-tab-bg-active-color',
            isLight ? tab.BackgroundColorActiveLight?.value : tab.BackgroundColorActiveDark?.value
          );

          addStyle(
            tabStyleOverride,
            '--horizontal-icon-nav-tab-border-color',
            isLight ? tab.BorderColorDefaultLight?.value : tab.BorderColorDefaultDark?.value
          );
          addStyle(
            tabStyleOverride,
            '--horizontal-icon-nav-tab-border-hover-color',
            isLight ? tab.BorderColorHoverLight?.value : tab.BorderColorHoverDark?.value
          );
          addStyle(
            tabStyleOverride,
            '--horizontal-icon-nav-tab-border-active-color',
            isLight ? tab.BorderColorActiveLight?.value : tab.BorderColorActiveDark?.value
          );

          addStyle(
            tabStyleOverride,
            '--horizontal-icon-nav-tab-color',
            isLight ? tab.FontColorDefaultLight?.value : tab.FontColorDefaultDark?.value
          );
          addStyle(
            tabStyleOverride,
            '--horizontal-icon-nav-tab-hover-color',
            isLight ? tab.FontColorHoverLight?.value : tab.FontColorHoverDark?.value
          );
          addStyle(
            tabStyleOverride,
            '--horizontal-icon-nav-tab-active-color',
            isLight ? tab.FontColorActiveLight?.value : tab.FontColorActiveDark?.value
          );

          addStyle(
            tabStyleOverride,
            '--horizontal-icon-nav-tab-icon-color',
            isLight
              ? tab.IconColorDefaultLight?.value.replace(';', '')
              : tab.IconColorDefaultDark?.value.replace(';', '')
          );
          addStyle(
            tabStyleOverride,
            '--horizontal-icon-nav-tab-icon-hover-color',
            isLight
              ? tab.IconColorHoverLight?.value.replace(';', '')
              : tab.IconColorHoverDark?.value.replace(';', '')
          );
          addStyle(
            tabStyleOverride,
            '--horizontal-icon-nav-tab-icon-active-color',
            isLight
              ? tab.IconColorActiveLight?.value.replace(';', '')
              : tab.IconColorActiveDark?.value.replace(';', '')
          );
        }

        const tracking = {
          'data-track': trackingAttributes['data-track'],
          'data-track-value': 'horizontaltab:' + tab.Title?.value,
          'data-trackt-destination': trackingAttributes['data-trackt-destination'],
        };

        return (
          <li key={tabRaw.id}>
            <div
              className={panelClassName.join(' ')}
              style={tabStyleOverride}
              tabIndex={0}
              data-panel={`anchor-${tabRaw.id}`}
              {...tracking}
            >
              {img}
              <div>{tab.Title.value}</div>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

function renderNavigationTabs(
  panelTabClassList,
  horizontalTabPanelLists,
  firstPanelItemId,
  trackingAttributes,
  isLight
) {
  return (
    <ul className={panelTabClassList.join(' ')}>
      {horizontalTabPanelLists.map((tabRaw) => {
        const tab = tabRaw.fields;

        const panelClassName = ['c-horizontal-tabs-panelNav-tab'];
        if (tabRaw.id === firstPanelItemId) panelClassName.push('is-active');

        const img = tab.Image?.value?.src && tab.Image?.value.class !== 'scEmptyImage' && (
          <img alt={tab.Image?.value?.alt || ''} src={tab.Image.value.src} />
        );

        const tabStyleOverride = {};

        if (tab.Image?.value?.src && tab.Image?.value.class !== 'scEmptyImage') {
          panelClassName.push('c-horizontal-tabs-icon-nav-tab');

          const invertedField = isLight ? tab.InvertedLight : tab.InvertedDark;
          if (invertedField?.value) panelClassName.push('is-inverted');

          const themeField = isLight ? tab.TabThemeLight : tab.TabThemeDark;
          if (themeField?.fields?.['ADS Color']?.value)
            panelClassName.push(`u-filter-${themeField?.fields?.['ADS Color']?.value}`);

          addStyle(
            tabStyleOverride,
            '--horizontal-icon-nav-tab-bg-color',
            isLight ? tab.BackgroundColorDefaultLight?.value : tab.BackgroundColorDefaultDark?.value
          );
          addStyle(
            tabStyleOverride,
            '--horizontal-icon-nav-tab-bg-hover-color',
            isLight ? tab.BackgroundColorHoverLight?.value : tab.BackgroundColorHoverDark?.value
          );
          addStyle(
            tabStyleOverride,
            '--horizontal-icon-nav-tab-bg-active-color',
            isLight ? tab.BackgroundColorActiveLight?.value : tab.BackgroundColorActiveDark?.value
          );

          addStyle(
            tabStyleOverride,
            '--horizontal-icon-nav-tab-border-color',
            isLight ? tab.BorderColorDefaultLight?.value : tab.BorderColorDefaultDark?.value
          );
          addStyle(
            tabStyleOverride,
            '--horizontal-icon-nav-tab-border-hover-color',
            isLight ? tab.BorderColorHoverLight?.value : tab.BorderColorHoverDark?.value
          );
          addStyle(
            tabStyleOverride,
            '--horizontal-icon-nav-tab-border-active-color',
            isLight ? tab.BorderColorActiveLight?.value : tab.BorderColorActiveDark?.value
          );

          addStyle(
            tabStyleOverride,
            '--horizontal-icon-nav-tab-color',
            isLight ? tab.FontColorDefaultLight?.value : tab.FontColorDefaultDark?.value
          );
          addStyle(
            tabStyleOverride,
            '--horizontal-icon-nav-tab-hover-color',
            isLight ? tab.FontColorHoverLight?.value : tab.FontColorHoverDark?.value
          );
          addStyle(
            tabStyleOverride,
            '--horizontal-icon-nav-tab-active-color',
            isLight ? tab.FontColorActiveLight?.value : tab.FontColorActiveDark?.value
          );

          addStyle(
            tabStyleOverride,
            '--horizontal-icon-nav-tab-icon-color',
            isLight
              ? tab.IconColorDefaultLight?.value.replace(';', '')
              : tab.IconColorDefaultDark?.value.replace(';', '')
          );
          addStyle(
            tabStyleOverride,
            '--horizontal-icon-nav-tab-icon-hover-color',
            isLight
              ? tab.IconColorHoverLight?.value.replace(';', '')
              : tab.IconColorHoverDark?.value.replace(';', '')
          );
          addStyle(
            tabStyleOverride,
            '--horizontal-icon-nav-tab-icon-active-color',
            isLight
              ? tab.IconColorActiveLight?.value.replace(';', '')
              : tab.IconColorActiveDark?.value.replace(';', '')
          );
        }

        const tracking = {
          'data-track': trackingAttributes['data-track'],
          'data-track-value': 'horizontaltab:' + tab.Title?.value,
          'data-trackt-destination': trackingAttributes['data-trackt-destination'],
        };

        return (
          <li key={tabRaw.id}>
            <div
              className={panelClassName.join(' ')}
              style={tabStyleOverride}
              tabIndex={0}
              data-panel={`anchor-${tabRaw.id}`}
              {...tracking}
            >
              {img}
              <div>{tab.Title.value}</div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

const renderTabPanelItem = (itemRaw, trackingAttributes, tab, index, ctaSecondaryCss) => {
  const item = itemRaw.fields;

  const layoutClass =
    item.HorizontalTabPanelItemLayout?.value === 'UpDown'
      ? 'c-horizontal-tabs-panel-content-promo-item--primary'
      : 'c-horizontal-tabs-panel-content-promo-item--secondary';

  const linkText = item.CallToAction?.value?.text;

  const itemTracking = {
    'data-track': trackingAttributes['data-track'],
    'data-track-value': 'horizontaltab:' + tab.Title?.value + ':' + linkText,
    'data-trackt-destination': trackingAttributes['data-trackt-destination'],
  };

  return (
    <div className={layoutClass} key={index}>
      <div className="c-horizontal-tabs-panel-content-promo-item__image">
        <Image field={item.Image} tabIndex="0" width="" height="" />
      </div>
      <div className="c-horizontal-tabs-panel-content-promo-item__detail">
        <div className="c-horizontal-tabs-panel-content-promo-item__title">
          {linkText && (
            <Link field={item.CallToAction} className={ctaSecondaryCss} {...itemTracking} />
          )}
        </div>
        {item.Description?.value && (
          <div className="c-horizontal-tabs-panel-content-promo-item__description" tabIndex="0">
            <RichText field={item.Description} />
          </div>
        )}
      </div>
    </div>
  );
};
